
import { Calendar } from 'antd'
import { DateThreshold } from 'components/Input/variations/Calendar/types'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import React, { useContext, useEffect } from 'react'
import { applyWeekdaysLabels, getYears } from './config'
import DateCellRender from './DateCellRender'
import HeaderRender from './HeaderRender'
import './index.scss'

type CalendarComponentProps = {
  startDate: string
  setStartDate: React.Dispatch<React.SetStateAction<string>>
  endDate: string
  setEndDate: React.Dispatch<React.SetStateAction<string>>
  firstValue: string
  setFirstValue: React.Dispatch<React.SetStateAction<string>>
  secondValue: string
  setSecondValue: React.Dispatch<React.SetStateAction<string>>
  calendarYearRange: number
}

function getStartAndEndDates(firstValue: string, secondValue: string) {
  const firstValueTime = new Date(firstValue).getTime()
  const secondValueTime = new Date(secondValue).getTime()

  return {
    start: dayjs(Math.min(firstValueTime, secondValueTime)).format('YYYY-MM-DD'),
    end: dayjs(Math.max(firstValueTime, secondValueTime)).format('YYYY-MM-DD')
  }
}

function getDateThreshold(calendarYearRange: number) {
  const years = getYears(new Date().getUTCFullYear(), calendarYearRange).map(year => year.value)

  return { start: dayjs(`${years[0]}-01-01`), end: dayjs(`${years[years.length - 1]}-12-31`) }
}

function getDisabledDates(date: Dayjs, { start, end }: DateThreshold) {
  return date.isBefore(start) || date.isAfter(end)
}

export default function CalendarComponent({
  startDate,
  setStartDate,
  setEndDate,
  firstValue,
  secondValue,
  setFirstValue,
  setSecondValue,
  calendarYearRange
}: CalendarComponentProps) {
  const t = useContext(I18nContext)
  const dateThreshold = getDateThreshold(calendarYearRange)

  useEffect(() => {
    if (firstValue) {
      if (secondValue) {
        const { start, end } = getStartAndEndDates(firstValue, secondValue)
        setStartDate(start)
        setEndDate(end)
      } else {
        setStartDate(dayjs(firstValue).format('YYYY-MM-DD'))
        setEndDate(dayjs(firstValue).format('YYYY-MM-DD'))
      }
    }
  }, [firstValue, secondValue])

  useEffect(() => {
    const table = document.getElementsByClassName('ant-picker-content')[0]
    if (table) {
      const weekdays = table.children[0].children[0].children
      applyWeekdaysLabels(weekdays, t)
    }
  }, [document.getElementsByClassName('ant-picker-content')[0]])

  return (
    <>
      <Calendar
        fullscreen={false}
        defaultValue={dayjs(startDate)}
        headerRender={(props) => <HeaderRender {...props} calendarYearRange={calendarYearRange} />}
        disabledDate={(date) => getDisabledDates(date, dateThreshold)}
        fullCellRender={(date) => <DateCellRender
          date={date}
          firstValue={firstValue}
          setFirstValue={setFirstValue}
          secondValue={secondValue}
          setSecondValue={setSecondValue}
          dateThreshold={dateThreshold}
        />}
      />
    </>
  )
}
